import {
  Button,
  CheckInput,
  OutlinedSelect,
  Select,
  SelectOptionType,
  TextAreaInput,
  TextInput,
  DateInput,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useEffect } from 'react';
import { Control, DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Form, FormGroup, Row } from 'reactstrap';

import { SectionTitle } from '../../text';
import { EXTRA_FORMATTERS } from '../utils';
import {
  FieldExtraFormatter,
  FormField,
  FormSection,
  validationSchema,
} from '../validations/validations';
import ConditionalFieldWrapper from './ConditionalFieldWrapper';
import ConditionalSectionWrapper from './ConditionalSectionWrapper';
import DependentSelect from './DependentSelect';

export type DynamicContactType = 'studentFile' | 'sustainerFile';

export interface ButtonData {
  type: 'button' | 'submit';
  text: string;
  onClick?: () => void;
  disabledInLoading?: boolean;
  disabledInSubmitting?: boolean;
  fullwidth?: boolean;
  outlined?: boolean;
  col?: {
    xs?: number;
    md?: number;
    sm?: number;
    lg?: number;
    xl?: number;
  };
}

function RenderFieldContent(
  field: FormField,
  control: Control<any>,
  options?: Record<string, SelectOptionType[]>,
  readOnly?: boolean,
) {
  const componentType = field.component || (field as any).type;

  // Use DependentSelect for select fields with dependency
  if (componentType === 'select' && field.dependency && options) {
    return (
      <DependentSelect
        field={field}
        dependency={field.dependency}
        allOptions={options}
        readOnly={readOnly}
      />
    );
  }

  return (
    <>
      {componentType === 'text' && !field?.properties?.outlined && (
        <TextInput
          {...{ ...field.properties, control }}
          name={field.name}
          label={field.label}
          maxLength={field.validations?.maxLength}
          formatter={
            EXTRA_FORMATTERS[
              field?.properties?.formatter as FieldExtraFormatter
            ] || field?.properties?.formatter
          }
          rules={validationSchema(field.validations)}
          placeholder={field?.placeholder}
          readOnly={readOnly}
        />
      )}
      {componentType === 'text' && field?.properties?.outlined && (
        <TextOutlinedInput
          {...{ ...field.properties, control }}
          name={field.name}
          label={field.label}
          maxLength={field.validations?.maxLength}
          formatter={
            EXTRA_FORMATTERS[
              field?.properties?.formatter as FieldExtraFormatter
            ] || field?.properties?.formatter
          }
          rules={validationSchema(field.validations)}
          placeholder={field?.placeholder}
          readOnly={readOnly}
        />
      )}
      {componentType === 'select' && !field?.properties?.outlined && (
        <Select
          {...{ ...field.properties, control }}
          name={field.name}
          label={field.label}
          options={
            field?.options ??
            (options &&
              options[field.optionKey || field.resourceName || field.name]) ??
            []
          }
          rules={validationSchema(field.validations, {
            isArray: !!field?.properties?.isMulti,
          })}
          placeholder={field?.placeholder}
          disabled={readOnly}
        />
      )}
      {componentType === 'select' && field?.properties?.outlined && (
        <OutlinedSelect
          {...{ ...field.properties, control }}
          name={field.name}
          label={field.label}
          options={
            field?.options ??
            (options &&
              options[field.optionKey || field.resourceName || field.name]) ??
            []
          }
          rules={validationSchema(field.validations, {
            isArray: !!field?.properties?.isMulti,
          })}
          placeholder={field?.placeholder}
          disabled={readOnly}
        />
      )}
      {componentType === 'textarea' && (
        <TextAreaInput
          {...{ ...field.properties, control }}
          name={field.name}
          label={field.label}
          maxLength={field.validations?.maxLength}
          rules={validationSchema(field.validations)}
          placeholder={field?.placeholder}
          readOnly={readOnly}
        />
      )}
      {componentType === 'date' && (
        <DateInput
          {...{ ...field.properties, control }}
          name={field.name}
          label={field.label}
          maxDate={field.validations?.maxDate}
          rules={validationSchema(field.validations)}
          disabled={field.properties?.disabled || readOnly}
        />
      )}
      {componentType === 'checkbox' && (
        <CheckInput
          control={control}
          name={field.name}
          label={field.label}
          disabled
        />
      )}
    </>
  );
}

function RenderFields(
  section: FormSection,
  control: Control<any>,
  options?: Record<string, SelectOptionType[]>,
  readOnly?: boolean,
) {
  return section.fields
    .filter((field: any) => {
      const type = field.component || (field as any).type;
      return type && !type.endsWith('-hidden');
    })
    .map((field: FormField, index: number) => {
      const fieldContent = (
        <Col key={index} md={field.grid?.md ?? 6} xs={field.grid?.xs ?? 12}>
          <FormGroup>
            {RenderFieldContent(field, control, options, readOnly)}
          </FormGroup>
        </Col>
      );

      if (field.conditional) {
        return (
          <ConditionalFieldWrapper
            key={index}
            conditional={field.conditional}
            fieldName={field.name}
          >
            {fieldContent}
          </ConditionalFieldWrapper>
        );
      }

      return fieldContent;
    });
}

interface RenderFormProps {
  structureBtns?: ButtonData[];
  structure: any;
  defaultValues: DefaultValues<any>;
  optionalValues: DefaultValues<any>;
  options?: Record<string, SelectOptionType[]>;
  sectionComponents?: Record<string, JSX.Element>;
  isModalRender?: boolean;
  onSubmit: (data: any) => Promise<void>;
  onErrors: (error: string[]) => void;
  readOnly?: boolean;
  source?: string;
}

function RenderForm(props: RenderFormProps) {
  const {
    structureBtns,
    isModalRender,
    structure,
    defaultValues,
    optionalValues,
    sectionComponents,
    options,
    source,
    onErrors,
    onSubmit,
    readOnly,
  } = props;
  const { t } = useTranslation();
  const history = useHistory();

  const buttons: ButtonData[] = [
    {
      type: 'button',
      text: t('common.actions.back'),
      onClick: () => history.push('/tuition-process'),
      disabledInSubmitting: true,
      fullwidth: true,
      outlined: true,
    },

    {
      type: 'submit',
      text: t('common.actions.next'),
      disabledInLoading: true,
      disabledInSubmitting: true,
      fullwidth: true,
    },
  ];

  const methods = useForm<{ hasOptionalValues: boolean; [key: string]: any }>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUnregister: true,
  });

  const { formState, setValue } = methods;

  const hasOptionalValues = methods.watch('hasOptionalValues');

  useEffect(() => {
    if (hasOptionalValues && Boolean(optionalValues)) {
      Object.entries(optionalValues).forEach(([key, value]) => {
        setValue(key, value);
      });
    }
  }, [hasOptionalValues, optionalValues, setValue]);

  const fieldNames = structure.flatMap((section: any) =>
    section.fields.map((field: any) => field.name),
  );

  structure
    .flatMap((section: any) => section.fields)
    .forEach((field: any) => {
      field.dependsOn?.forEach((dependency: any) => {
        if (!fieldNames.includes(dependency)) {
          onErrors([
            `El campo "${field.name}" depende de "${dependency}", que no está en el formulario.`,
          ]);
        }
      });
    });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit)}>
        {structure.map((section: FormSection, index: number) => {
          if (section?.source !== undefined && !source?.trim()) {
            return <></>;
          } else if (
            typeof section?.source === 'string' &&
            source?.trim() !== section?.source?.trim()
          ) {
            return <></>;
          } else if (
            Array.isArray(section?.source) &&
            (!source?.trim() || !section?.source?.includes(source?.trim()))
          ) {
            return <></>;
          }
          if (section.component && sectionComponents?.[section.component]) {
            return sectionComponents[section.component];
          } else if (
            section.component &&
            !sectionComponents?.[section.component]
          ) {
            return (
              <div key={index} className="pt-5 text-center">
                <SectionTitle
                  text={`No has definido un componente para la sección ${section.title}`}
                />
                <p>Sección sin definir</p>
              </div>
            );
          } else {
            const sectionContent = (
              <div key={index} className="pt-5">
                <SectionTitle
                  text={section.title}
                  tooltipText={section.tooltip?.text}
                  tooltipId={section.tooltip?.id}
                />
                <Row>
                  {RenderFields(section, methods.control, options, readOnly)}
                </Row>
              </div>
            );

            if (section.conditional) {
              return (
                <ConditionalSectionWrapper
                  key={index}
                  conditional={section.conditional}
                  section={section}
                >
                  {sectionContent}
                </ConditionalSectionWrapper>
              );
            }

            return sectionContent;
          }
        })}

        <Row
          className={
            isModalRender
              ? 'pt-5 justify-content-center'
              : 'py-5 justify-content-end'
          }
        >
          {(structureBtns ?? buttons).map((button, index) => (
            <Col
              {...button?.col}
              key={index}
              xs={button?.col?.xs || 12}
              lg={button?.col?.lg || 4}
              className={`pb-2 order-${index + 1} order-lg-${index + 1}`}
            >
              <Button
                type={button.type}
                text={button.text}
                onClick={button.onClick}
                loading={
                  button.disabledInLoading ? formState.isSubmitting : false
                }
                disabled={
                  button.disabledInSubmitting ? formState.isSubmitting : false
                }
                fullwidth={button.fullwidth}
                outlined={button.outlined}
              />
            </Col>
          ))}
        </Row>
      </Form>
    </FormProvider>
  );
}

export default RenderForm;
